/**
 * A família aberta (teste físico, itens 24 e 26): quem está nela, e as ações.
 *
 *  - quem está na família (ou quem administra a viagem) renomeia, troca a
 *    cor e dissolve;
 *  - quem está nela (ou quem administra) **convida mais gente** — sem limite,
 *    cada convite com aceite (PO, 20/09/2026: antes só dava no ato de criar);
 *  - quem está nela pode sair.
 *
 * Dissolver pergunta antes: todo mundo sai, e a divisão por família passa a
 * contar cada um como uma unidade.
 */
import { familyPalette } from '@nv/tokens';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { MembroDaViagem } from '../../trip/TripContext';
import { Sheet } from '../../ui/Sheet';
import { ConfirmSheet } from '../../ui/primitives';
import { Field } from '../auth/Field';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { dissolveFamily, type FamilyView, GrupoError, leaveFamily, updateFamily } from './api';
import { ConvidarParaFamiliaSheet } from './ConvidarParaFamiliaSheet';

export function FamiliaSheet({
  tripId,
  familia,
  membros,
  meuMemberId,
  podeAdministrar,
  onFechar,
}: {
  tripId: string;
  familia: FamilyView;
  membros: MembroDaViagem[];
  meuMemberId: string;
  /** `manage_members`: dono e admin mexem em qualquer família. */
  podeAdministrar: boolean;
  onFechar: () => void;
}) {
  const cliente = useQueryClient();
  const souDela = familia.memberIds.includes(meuMemberId);
  const podeMexer = souDela || podeAdministrar;
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(familia.name);
  const [cor, setCor] = useState(familia.color);
  const [confirmando, setConfirmando] = useState<'dissolver' | 'sair' | 'convidar' | null>(null);

  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey: ['grupo', tripId] });
    void cliente.invalidateQueries({ queryKey: ['viagem'] });
  };

  const salvar = useMutation({
    mutationFn: () => updateFamily(tripId, familia.id, { name: nome, color: cor }),
    onSuccess: () => {
      invalidar();
      setEditando(false);
    },
  });
  const dissolver = useMutation({
    mutationFn: () => dissolveFamily(tripId, familia.id),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
  });
  const sair = useMutation({
    mutationFn: () => leaveFamily(tripId),
    onSuccess: () => {
      invalidar();
      onFechar();
    },
  });

  const pessoas = familia.memberIds.map((id) => membros.find((m) => m.memberId === id)).filter((m): m is MembroDaViagem => Boolean(m));
  const erro = (salvar.error ?? dissolver.error ?? sair.error) as GrupoError | null;

  if (confirmando === 'convidar') {
    return (
      <ConvidarParaFamiliaSheet
        tripId={tripId}
        familia={familia}
        membros={membros}
        meuMemberId={meuMemberId}
        onFechar={() => setConfirmando(null)}
      />
    );
  }
  if (confirmando === 'dissolver') {
    return (
      <ConfirmSheet
        titulo={`Dissolver a ${familia.name}?`}
        texto={`${pessoas.length === 1 ? '1 pessoa sai' : `${pessoas.length} pessoas saem`} da família e passam a contar, cada uma, como uma unidade na divisão. As despesas já divididas continuam como estão.`}
        confirmar="Dissolver família"
        ocupado={dissolver.isPending}
        testId="confirmar-dissolver"
        onCancel={() => setConfirmando(null)}
        onConfirm={() => dissolver.mutate()}
      />
    );
  }
  if (confirmando === 'sair') {
    return (
      <ConfirmSheet
        titulo="Sair da família?"
        texto="Suas despesas já divididas continuam como estão. As próximas passam a contar você como uma unidade."
        confirmar="Sair da família"
        ocupado={sair.isPending}
        testId="confirmar-sair"
        onCancel={() => setConfirmando(null)}
        onConfirm={() => sair.mutate()}
      />
    );
  }

  return (
    <Sheet titleId="familia-titulo" onFechar={onFechar} testId="familia-sheet">
      {editando ? (
        <>
          <h2 className="sheet__title" id="familia-titulo">Editar família</h2>
          <Field label="Nome da família" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={60} required />
          <span className="nv-section-label">Cor</span>
          <div className="dia-cores" role="radiogroup" aria-label="Cor da família" data-testid="familia-cores">
            {familyPalette.map((c) => (
              <button
                type="button"
                key={c}
                role="radio"
                aria-checked={cor === c}
                aria-label={`Cor ${c}`}
                className={`dia-cor${cor === c ? ' dia-cor--ativa' : ''}`}
                style={{ background: c }}
                onClick={() => setCor(c)}
                data-testid={`familia-cor-${c.slice(1).toLowerCase()}`}
              />
            ))}
          </div>
          {erro && <p className="auth-error" role="alert">{erro.message}</p>}
          <div className="fin__ctas">
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setEditando(false)}>
              Cancelar
            </button>
            <button type="button" className="nv-btn nv-btn--primary" disabled={salvar.isPending || nome.trim().length === 0} onClick={() => salvar.mutate()} data-testid="familia-salvar">
              {salvar.isPending ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="familia__cab">
            <span className="grupo__ponto" style={{ background: familia.color }} aria-hidden="true" />
            <h2 className="sheet__title" id="familia-titulo">{familia.name}</h2>
          </div>
          <ul className="familia__lista" aria-label="Quem está na família">
            {pessoas.map((m) => (
              <li className="familia__pessoa" key={m.memberId}>
                <FotoAvatar nome={m.name} url={m.avatarUrl} cor={familia.color} tamanho={34} />
                <span className="familia__pessoa-nome">{m.name}</span>
                {m.memberId === meuMemberId && <span className="grupo__papel">você</span>}
              </li>
            ))}
            {pessoas.length === 0 && <li className="auth-note">Ninguém entrou ainda.</li>}
          </ul>
          {familia.pendingInvites > 0 && (
            <p className="auth-note">
              {familia.pendingInvites === 1 ? '1 convite aguardando resposta.' : `${familia.pendingInvites} convites aguardando resposta.`}
            </p>
          )}
          {erro && <p className="auth-error" role="alert">{erro.message}</p>}
          {podeMexer && (
            <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={() => setConfirmando('convidar')} data-testid="familia-convidar">
              + Convidar para a família
            </button>
          )}
          {podeMexer && (
            <div className="fin__ctas">
              <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setEditando(true)} data-testid="familia-editar">
                Editar
              </button>
              <button type="button" className="nv-btn nv-btn--ghost-danger" onClick={() => setConfirmando('dissolver')} data-testid="familia-dissolver">
                Dissolver
              </button>
            </div>
          )}
          {souDela && (
            <button type="button" className="nv-btn nv-btn--ghost-danger nv-btn--block" onClick={() => setConfirmando('sair')} data-testid="familia-sair">
              Sair da minha família
            </button>
          )}
        </>
      )}
    </Sheet>
  );
}
